// src/components/ui/sections/OperationalSettingsSection/OperationalSettingsSectionShadcn.jsx
import React from 'react';
import { Save } from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Switch } from '../../switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../select';
import { Card, CardContent, CardHeader, CardTitle } from '../../card';

import { cn } from '../../../../utils/cn';

const OperationalSettingsSectionShadcn = ({
  operationalForm,
  setOperationalForm,
  handleOperationalSubmit,
  savingSettings,
  hasChanges,
  paymentTermsOptions,
  className = ''
}) => {
  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleOperationalSubmit} className="space-y-6">
          {/* Scheduling Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Scheduling Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="min_notice_hours">Minimum Notice Hours</Label>
                  <Input
                    id="min_notice_hours"
                    type="number"
                    value={operationalForm.min_notice_hours}
                    onChange={(e) => setOperationalForm(prev => ({...prev, min_notice_hours: parseInt(e.target.value)}))}
                    min="0"
                  />
                  <p className="text-xs text-muted-foreground">Hours in advance customers must book</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="travel_buffer">Travel Buffer (minutes)</Label>
                  <Input
                    id="travel_buffer"
                    type="number"
                    value={operationalForm.travel_buffer}
                    onChange={(e) => setOperationalForm(prev => ({...prev, travel_buffer: parseInt(e.target.value)}))}
                    min="0"
                  />
                  <p className="text-xs text-muted-foreground">Buffer time between appointments</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <div className="space-y-2">
                  <Label htmlFor="max_concurrent">Max Concurrent Appointments</Label>
                  <Input
                    id="max_concurrent"
                    type="number"
                    value={operationalForm.max_concurrent_appointments}
                    onChange={(e) => setOperationalForm(prev => ({...prev, max_concurrent_appointments: parseInt(e.target.value)}))}
                    min="1"
                  />
                </div>

                <div className="flex items-center space-x-3 pt-8">
                  <Switch
                    id="allow_overlapping"
                    checked={operationalForm.allow_overlapping_bookings}
                    onCheckedChange={(checked) => setOperationalForm(prev => ({...prev, allow_overlapping_bookings: checked}))}
                  />
                  <Label htmlFor="allow_overlapping" className="text-sm font-medium">
                    Allow overlapping bookings
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Tax Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <Switch
                  id="tax_enabled"
                  checked={operationalForm.tax_enabled}
                  onCheckedChange={(checked) => setOperationalForm(prev => ({...prev, tax_enabled: checked}))}
                />
                <Label htmlFor="tax_enabled" className="text-sm font-medium">
                  Enable tax calculations
                </Label>
              </div>

              {operationalForm.tax_enabled && (
                <div className="ml-7 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tax_rate">Tax Rate (%)</Label>
                    <Input
                      id="tax_rate"
                      type="number"
                      step="0.01"
                      value={operationalForm.tax_rate}
                      onChange={(e) => setOperationalForm(prev => ({...prev, tax_rate: parseFloat(e.target.value)}))}
                      min="0"
                      max="100"
                      className="w-32"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Terms */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Default Payment Terms</Label>
                <Select
                  value={operationalForm.default_payment_terms}
                  onValueChange={(value) => setOperationalForm(prev => ({...prev, default_payment_terms: value}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentTermsOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>

      {/* Fixed Footer with Save Button */}
      <div className="flex-shrink-0 p-6 border-t">
        <div className="flex items-center justify-end">
          <Button
            onClick={handleOperationalSubmit}
            disabled={!hasChanges || savingSettings}
            size="lg"
            className="shadow-lg"
          >
            {savingSettings ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OperationalSettingsSectionShadcn;